"use client";

import { useEffect, useState } from "react";

import { CamundaHistoryService } from "@/lib/camunda/history-service";
import type { CamundaTask } from "@/lib/camunda/types";

const EMPTY_TITULOS_BY_PROCESS_INSTANCE_ID = new Map<string, string | null>();

function tasksTitulosCacheKey(tasks: CamundaTask[]): string {
  const processInstanceIds = tasks
    .map((t) => t.processInstanceId || null)
    .filter((value): value is string => value !== null);

  return [...new Set(processInstanceIds)].sort().join("|");
}

function processInstanceIdsFromCacheKey(cacheKey: string): string[] {
  if (cacheKey === "") return [];

  return [
    ...new Set(
      cacheKey
        .split("|")
        .filter((value): value is string => Boolean(value)),
    ),
  ];
}

export function useTaskListTitulos(tasks: CamundaTask[]) {
  const [titulosState, setTitulosState] = useState<{
    cacheKey: string;
    values: Map<string, string | null>;
  }>(() => ({
    cacheKey: "",
    values: EMPTY_TITULOS_BY_PROCESS_INSTANCE_ID,
  }));

  const cacheKey = tasksTitulosCacheKey(tasks);

  useEffect(() => {
    const processInstanceIds = processInstanceIdsFromCacheKey(cacheKey);

    if (processInstanceIds.length === 0) {
      return;
    }

    let cancelled = false;

    void CamundaHistoryService.getTitulosForProcessInstances(processInstanceIds)
      .then((map) => {
        if (!cancelled) {
          setTitulosState((previous) => {
            const values = new Map(previous.values);
            map.forEach((titulo, processInstanceId) => {
              values.set(processInstanceId, titulo);
            });

            return { cacheKey, values };
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTitulosState((previous) => ({ cacheKey, values: previous.values }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey]);

  const hasProcessInstances = cacheKey !== "";
  const titulosReady =
    !hasProcessInstances || titulosState.cacheKey === cacheKey;

  return { titulosByProcessInstanceId: titulosState.values, titulosReady };
}
